import GridDefinitions from "../../defaults/GridDefinitions.json";
import FieldNames from "../../defaults/FieldNames.json";
import {setShowGlobalLoader} from "../../redux/slices/globalContextSlice";
import store from "../../redux/store.tsx";
import {setFeature, setProject} from "../../redux/slices/detailsSlice";
import ErrorService from "../../services/ErrorService";
import {ResetFeatureFundingLinesByFeature} from "../../utils/FundingHelper";
import {getLegacyModifiedAttributes, getWFFTSModifiedAttributes, isFeaturePostFire} from "../../utils/FeatureHelper";
import {setDerivationError, setDerivationErrorOpen} from "../../redux/slices/errorSlice";
import DerivationService from "../../services/DerivationService";
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';

export const handleEstablishGridColumns = (setFeatureHistoryCols) => {

    const featureAuditLogGridColDefinitions = [
        GridDefinitions.UserAuditLog.modifiedBy,
        GridDefinitions.UserAuditLog.modifiedOn,
        GridDefinitions.UserAuditLog.modification,
    ];

    const establishGridColumns = (setFeatureHistoryCols) => {
        let columns = [];
        featureAuditLogGridColDefinitions.forEach((colDef) => {
            columns.push({
                field: colDef.value,
                headerName: colDef.header,
                type: colDef.type,
                hideable: colDef.hideable,
                sortable: colDef.sortable,
                editable: colDef.editable,
                flex: colDef.flex,
                align: colDef.align,
                disableExport: colDef.disableExport,
                valueGetter: (value) => {
                    return value; // Used for formatting string values I.E. currency formatting
                },
            });
        });

        setFeatureHistoryCols(columns);
    };

    establishGridColumns(setFeatureHistoryCols);
};

export const handleExpandedChanged = (e, id, expandedItems, setExpandedItems) => {
    let currentItem = expandedItems[id];
    if (currentItem === 'true') {
        currentItem = 'false';
    } else {
        currentItem = 'true';
    }
    let updatedValue = {};
    updatedValue = {[id]: currentItem};
    setExpandedItems((expandedItems) => ({
        ...expandedItems,
        ...updatedValue,
    }));
}

export const calculateEditableFiscalYearOffsetByDay = (value) => {
    //Fiscal Quarters are Q1 = 10/1 to 12/31, Q2 = 1/1 to 3/31, Q3 = 4/1 to 6/31, Q4 = 7/1 to 9/31
    if (value) {
        const month = value.getMonth() + 1;
        const year = value.getFullYear();
        const day = value.getDate();

        //Default state is for the current year fiscal year. The current year and fiscal year match
        let fiscalYear = year;

        // We are checking the 15th specifically for locking editing / displaying a warning. Normally FY rollover starts on October 1st
        if (month === 10 && day <= 15) {
            fiscalYear = year;
        } else if ((month === 10 && day > 15) || (month >= 11 && month <= 12)) {
            fiscalYear = year + 1;
        }
        return fiscalYear;
    }
};

export const findOcc = (arr, key) => {
    let arr2 = [];

    arr.forEach((x) => {
        // Checking if there is any object in arr2
        // which contains the key value
        if (
            arr2.some((val) => {
                return val[key] === x[key];
            })
        ) {
            // If yes! then increase the occurrence by 1
            arr2.forEach((k) => {
                if (k[key] === x[key]) {
                    k['occurrence']++;
                }
            });
        } else {
            // If not! Then create a new object initialize
            // it with the present iteration key's value and
            // set the occurrence to 1
            let a = {};
            a[key] = x[key];
            a['occurrence'] = 1;
            arr2.push(a);
        }
    });

    return arr2;
};

export const rederiveFeatureInfo = async (
    currentFeature,
    isTreatment,
    isFeatureActuals,
    isPoint,
    wfftsAttributes,
    legacyAttributes,
    resetAcreage = true
) => {
    /* Retrieve necessary state and config from store */
    const userRoleInfo = store.getState().globalContext.userRoleInfo;
    const config = store.getState().configuration.config;
    const isProject = store.getState().details.isProject;

    /* Loading state */
    store.dispatch(setShowGlobalLoader(true));

    /* Early return for no feature or attributes passed */
    if (!currentFeature || !currentFeature.attributes) {
        console.error('rederiveFeatureInfo called with no currentFeature or no attributes');
        store.dispatch(setShowGlobalLoader(false));
        return;
    }

    /* Setup reuseable variables */
    const currFeatAttr = currentFeature.attributes;
    const featureFields = FieldNames.featureFields;

    /* Early return if Program Management Points funded by National or Regional units */
    const fundingUnitType = currFeatAttr[featureFields.fundingUnitType];

    const isNationalOrRegional =
        fundingUnitType === FieldNames.unitTypes.national ||
        fundingUnitType === FieldNames.unitTypes.regional;

    const isProgramManagement =
        currFeatAttr[featureFields.category] ===
        FieldNames.categoriesForChecking.programManagement;

    if (
        currFeatAttr[featureFields.isPoint] === FieldNames.yesNo.yes &&
        isNationalOrRegional &&
        isProgramManagement
    ) {
        store.dispatch(setShowGlobalLoader(false));
        return;
    }

    /* Setup reuseable variables and flags */
    const isFeatureActualOrWFFTS = isFeatureActuals || (!isFeatureActuals && wfftsAttributes);
    const perimeter = geometryJsonUtils.fromJSON(currentFeature.geometry);
    const isPostFire = isFeaturePostFire(currFeatAttr);
    const tempPeri = currentFeature.geometry;
    const isNew = false;
    const geometryType = currentFeature?.attributes[featureFields.joinFeature];
    const isVegetationManual =
        currFeatAttr[featureFields.isVegetationManual] === FieldNames.yesNo.yes;

    /* Primary business logic - Begin derivation */
    DerivationService.deriveAttributesForGeometry(
        perimeter,
        isNew,
        isTreatment,
        geometryType,
        isFeatureActuals,
        config,
        isVegetationManual,
        isPostFire,
        isPoint
    )
        .then((derivedAttr) => {
            let tempAttr;

            if (derivedAttr && derivedAttr.attributes) {
                tempAttr = { ...currentFeature.attributes, ...derivedAttr.attributes };
                if (derivedAttr.error) {
                    // TODO - EVAL: Imitating existing logic placement - this seems more like an edge-case
                    //  message than an error from investigation - need more eyes on it.
                    store.dispatch(setDerivationError(derivedAttr.error));
                    store.dispatch(setDerivationErrorOpen(true));
                }
            } else {
                tempAttr = { ...currentFeature.attributes };
            }

            const attrObjectID = tempAttr[featureFields.objectID];

            if (isFeatureActualOrWFFTS) {
                // NOTE: WFFTS and Legacy attributes must be modified after derivation. Mutates primary input 'tempAttr' and returns after modification.
                if (wfftsAttributes) {
                    tempAttr = getWFFTSModifiedAttributes(
                        tempAttr,
                        isTreatment,
                        isFeatureActuals,
                        wfftsAttributes
                    );
                } else if (legacyAttributes) {
                    tempAttr = getLegacyModifiedAttributes(
                        tempAttr,
                        legacyAttributes,
                        featureFields
                    );
                } else {
                    // TODO - VERIFY: Presumably this condition is for isFeatureActual === true
                    tempAttr = getLegacyModifiedAttributes(tempAttr, null, featureFields);
                }
            }

            const handleSetTempFeature = (updatedAttributes) => {
                const updatedFeature = {
                    attributes: updatedAttributes,
                    geometry: tempPeri,
                };
                store.dispatch(setFeature(updatedFeature));
                isProject && store.dispatch(setProject(updatedFeature));
                store.dispatch(setShowGlobalLoader(false));
            };

            if (attrObjectID && resetAcreage) {
                ResetFeatureFundingLinesByFeature(tempAttr).then((resp) => {
                    tempAttr[featureFields.isFundingAcresManual] = FieldNames.yesNo.no;
                    handleSetTempFeature(tempAttr);
                });
            } else {
                handleSetTempFeature(tempAttr);
            }
        })
        .catch((error) => {
            ErrorService.addErrorLogEntry(
                error,
                userRoleInfo[FieldNames.userFields.nifcUsername],
            );
            store.dispatch(setShowGlobalLoader(false));
        });
};